import { Action, createFeatureSelector, createReducer, createSelector, on } from '@ngrx/store';
import { AlertType, OptionType, StepState } from 'src/app/core/enums/enums';
import { PenaltyOverride } from 'src/app/core/models/cancellation-overrides.model';
import { CommissionRefund, QuoteRequest } from 'src/app/core/models/itinerary.model';
import { ContractError } from 'src/app/core/models/models';
import { Step } from 'src/app/core/models/step.model';
import { CancellationActions } from '../actions';
import {
  CancellationConfirm,
  CancellationQuote,
  PaymentChannel,
  PenaltyFareBasis,
  PenaltyFareBasisMachineLearning,
  RefundFare
} from 'src/app/core/models/cancellation.model';

export interface CancellationState {
  receivedFrom: string;
  cancellationQuote: CancellationQuote;
  penaltyFareBasis: PenaltyFareBasis[];
  penaltyMLFareBasis: PenaltyFareBasisMachineLearning;
  voidAllowed: boolean;
  quoteRequest: QuoteRequest;
  paymentChannel: PaymentChannel;
  paymentChannelsByCountry: PaymentChannel[];
  messageTypePaymentChannel: string;
  refundfare: RefundFare;
  steps: Step[];
  messageTypeStep2: string;
  loading: boolean;
  loadingPenalties: boolean;
  loadingEdit: boolean;
  overrideError: ContractError;
  cancellationError: ContractError;
  splitError: ContractError;
  activeGdsRefundSwitch: boolean;
  pnrCancellationQuote: CancellationConfirm;
  penaltiesOverride: PenaltyOverride[];
  errorMachineLearning: boolean;
  sendFeedbackML: boolean;
}

export const initialState: CancellationState = {
  cancellationQuote: null,
  penaltyFareBasis: null,
  penaltyMLFareBasis: null,
  voidAllowed: false,
  quoteRequest: null,
  paymentChannel: null,
  paymentChannelsByCountry: null,
  messageTypePaymentChannel: 'default',
  refundfare: null,
  steps: [
    { title: 'cancellation-info.step1', state: StepState.Active },
    { title: 'cancellation-info.step2', state: StepState.Normal }
  ],
  messageTypeStep2: null,
  pnrCancellationQuote: null,
  loading: false,
  loadingPenalties: true,
  loadingEdit: false,
  overrideError: null,
  splitError: null,
  cancellationError: null,
  activeGdsRefundSwitch: false,
  penaltiesOverride: null,
  errorMachineLearning: null,
  sendFeedbackML: null,
  receivedFrom: null
};

export function reducer(state: CancellationState | undefined, action: Action): CancellationState {
  return cancellationReducer(state, action);
}

const cancellationReducer = createReducer(
  initialState,
  on(CancellationActions.LoadCancellationSuccess, (state, { cancellationQuote }) => ({
    ...initialState,
    quoteRequest: {
      ...state.quoteRequest,
      commissions: cancellationQuote.refundFare.fareSummaries.map(
        (type) =>
          ({
            passengerType: type.passengerType,
            passengerCount: type.passengerCount
          } as CommissionRefund)
      )
    },
    cancellationQuote: {
      ...cancellationQuote,
      quoteOverrides: {
        ...cancellationQuote.quoteOverrides,
        receivedFrom: state.receivedFrom,
        pnr: cancellationQuote.pnr,
        bookingCode: cancellationQuote.bookingCode,
        gds: cancellationQuote.gds,
        country: cancellationQuote.country,
        isGdsRefundOverride: cancellationQuote.isGdsRefund
      }
    },
    voidAllowed: cancellationQuote.voidAllowed,
    activeGdsRefundSwitch: cancellationQuote.isGdsRefund
  })),
  on(CancellationActions.CreatePenaltiesOverride, (state, { penaltiesOverride }) => ({
    ...state,
    penaltiesOverride
  })),
  on(CancellationActions.ChoosePaymentChannel, (state, { paymentChannel }) => ({
    ...state,
    paymentChannel,
    messageTypePaymentChannel: !!paymentChannel ? AlertType.Default : AlertType.Error
  })),
  on(CancellationActions.LoadPaymentChannelSuccess, (state, { paymentChannelsByCountry }) => ({
    ...state,
    paymentChannelsByCountry,
    messageTypePaymentChannel: AlertType.Default,
    loading: false
  })),
  on(CancellationActions.LoadPaymentChannelFail, (state, { error }) => ({
    ...state,
    messageTypePaymentChannel: AlertType.Error,
    loading: false,
    paymentChannel: null
  })),
  on(CancellationActions.CancelPnrRequest, (state, { cancellationQuoteRequest }) => ({
    ...state,
    loading: true,
    cancellationError: null
  })),
  on(CancellationActions.CancelPnrSuccess, (state, { pnrCancellationQuote }) => ({
    ...state,
    pnrCancellationQuote,
    cancellationQuote: pnrCancellationQuote.cancellationQuote,
    loading: false,
    steps:
      !pnrCancellationQuote.isComplete && !state.voidAllowed
        ? [
            { title: 'cancellation-info.step1', state: StepState.Completed },
            { title: 'cancellation-info.step2', state: StepState.Error }
          ]
        : [
            { title: 'cancellation-info.step1', state: StepState.Completed },
            { title: 'cancellation-info.step2', state: StepState.Completed }
          ],
    messageTypeStep2:
      !pnrCancellationQuote.isComplete && !state.voidAllowed ? AlertType.Warning : AlertType.Success
  })),
  on(CancellationActions.CancelPnrFail, (state, { cancellationError }) => ({
    ...state,
    cancellationError,
    steps: [
      { title: 'cancellation-info.step1', state: StepState.Completed },
      { title: 'cancellation-info.step2', state: StepState.Error }
    ],
    loading: false,
    messageTypeStep2: AlertType.Error
  })),
  on(CancellationActions.goToStep2, (state) => ({
    ...state,
    steps: [
      { title: 'cancellation-info.step1', state: StepState.Completed },
      { title: 'cancellation-info.step2', state: StepState.Active }
    ]
  })),
  on(CancellationActions.goToStep1, (state) => ({
    ...state,
    steps: [
      { title: 'cancellation-info.step1', state: StepState.Active },
      { title: 'cancellation-info.step2', state: StepState.Normal }
    ]
  })),
  on(CancellationActions.SplitPnrRequest, (state) => ({
    ...state,
    loading: true,
    splitError: null
  })),
  on(CancellationActions.SplitPnrSuccess, (state, { cancellationQuote }) => ({
    ...state,
    loading: false,
    cancellationQuote,
    steps: [
      { title: 'cancellation-info.step1', state: StepState.Completed },
      { title: 'cancellation-info.step2', state: StepState.Active }
    ],
    quoteRequest: { ...state.quoteRequest, pnr: cancellationQuote.pnr }
  })),
  on(CancellationActions.SplitPnrFail, (state, { cancellationError }) => ({
    ...state,
    loading: false,
    splitError: cancellationError
  })),
  on(CancellationActions.OverrideQuoteRequest, (state, { quoteOverrides }) => ({
    ...state,
    loadingEdit: true,
    overrideError: null
  })),
  on(CancellationActions.SetReceivedFrom, (state, { receivedFrom }) => ({
    ...state,
    receivedFrom
  })),
  on(CancellationActions.RemoveReceivedFrom, (state) => ({
    ...state,
    receivedFrom: null
  })),
  on(CancellationActions.OverrideQuoteSuccess, (state, { cancellationQuote }) => ({
    ...state,
    loadingEdit: false,
    activeGdsRefundSwitch: cancellationQuote.quoteOverrides.isGdsRefundOverride,
    cancellationQuote: {
      ...cancellationQuote,
      quoteOverrides: {
        ...cancellationQuote.quoteOverrides,
        pnr: state.cancellationQuote.pnr,
        receivedFrom: state.receivedFrom,
        bookingCode: state.cancellationQuote.bookingCode,
        gds: state.cancellationQuote.gds,
        country: state.cancellationQuote.country,
        noRefundablePenalties: cancellationQuote.quoteOverrides.noRefundablePenalties
      }
    }
  })),
  on(CancellationActions.OverrideQuoteFail, (state, { error }) => ({
    ...state,
    loadingEdit: false,
    overrideError: error
  })),
  on(CancellationActions.ToggleIsGdsRefundSwitch, (state, { activeGdsRefundSwitch }) => ({
    ...state,
    activeGdsRefundSwitch,
    cancellationQuote: {
      ...state.cancellationQuote,
      quoteOverrides: {
        ...state.cancellationQuote.quoteOverrides,
        isGdsRefundOverride: activeGdsRefundSwitch
      }
    }
  })),
  on(CancellationActions.AddCancellationCode, (state, { cancellationCode }) => ({
    ...state,
    quoteRequest: {
      ...state.quoteRequest,
      cancellationCode
    }
  })),
  on(CancellationActions.StoreQuoteRequest, (state, { quoteRequest }) => ({
    ...state,
    quoteRequest,
    error: null,
    splitError: null
  })),
  on(CancellationActions.AddCommissions, (state, { commissions }) => ({
    ...state,
    quoteRequest: { ...state.quoteRequest, commissions }
  })),

  on(CancellationActions.OverrideQuoteFail, (state, { error }) => ({
    ...state,
    loadingEdit: false,
    overrideError: error
  })),

  on(CancellationActions.loadingPenaltiesRequest, (state) => ({
    ...state,
    loadingPenalties: true
  })),

  on(CancellationActions.loadPenaltiesSuccess, (state, { penaltyFareBasis }) => ({
    ...state,
    penaltyFareBasis: penaltyFareBasis,
    loadingPenalties: false,
    loading: false
  })),
  on(CancellationActions.loadPenaltiesMLSuccess, (state, { penaltyMLFareBasis }) => ({
    ...state,
    penaltyMLFareBasis,
    loadingPenalties: false,
    loading: false
  })),
  on(CancellationActions.errorLoadMachineLearningParameter, (state) => ({
    ...state,
    errorMachineLearning: true
  })),
  on(CancellationActions.sendFeedbackMLSuccess, (state) => ({
    ...state,
    sendFeedbackML: true
  }))
);

export const getCancellationState = createFeatureSelector<CancellationState>(
  OptionType.Cancellation
);

export const getPenaltyFareBasis = createFeatureSelector<CancellationState>(
  OptionType.Cancellation
);

export const getCancellationQuote = createSelector(
  getCancellationState,
  (state) => state.cancellationQuote
);

export const getPenaltyFareBasisQuote = createSelector(
  getPenaltyFareBasis,
  (state) => state.penaltyFareBasis
);

export const getPenaltyMLFareBasis = createSelector(
  getPenaltyFareBasis,
  (state) => state.penaltyMLFareBasis
);

export const loadingPenaltiesEffect = createSelector(getPenaltyFareBasis, (state) => {
  return state.loadingPenalties;
});

export const getQuoteRequest = createSelector(getCancellationState, (state) => state.quoteRequest);

export const getPaymentChannel = createSelector(
  getCancellationState,
  (state) => state.paymentChannel
);

export const getPaymentChannelByCountry = createSelector(getCancellationState, (state) => {
  return (state.paymentChannelsByCountry || []).filter(
    (channel: PaymentChannel) => channel.isActive
  );
});

export const getRefundfare = createSelector(getCancellationState, (state) => state.refundfare);

export const getMessageTypeStep2 = createSelector(
  getCancellationState,
  (state) => state.messageTypeStep2
);

export const getCancellationConfirm = createSelector(
  getCancellationState,
  (state) => state.pnrCancellationQuote
);

export const getLoading = createSelector(getCancellationState, (state) => state.loading);

export const getError = createSelector(getCancellationState, (state) => state.overrideError);

export const getSplitError = createSelector(getCancellationState, (state) => state.splitError);

export const getCancellationError = createSelector(
  getCancellationState,
  (state) => state.cancellationError
);

export const getMessageTypePaymentChannel = createSelector(
  getCancellationState,
  (state) => state.messageTypePaymentChannel
);

export const getActiveGdsRefundSwitch = createSelector(
  getCancellationState,
  (state) => state.activeGdsRefundSwitch
);

export const getLoadingEdit = createSelector(getCancellationState, (state) => state.loadingEdit);

export const getSteps = createSelector(getCancellationState, (state) => state.steps);

export const getHighlightCategories = createSelector(getPenaltyFareBasis, (state) => {
  let penalties: PenaltyFareBasis[];
  penalties = state.penaltyFareBasis;
  penalties = !!penalties ? penalties : [];
  return penalties;
});

export const getPenaltiesOverride = createSelector(
  getCancellationState,
  (state) => state.penaltiesOverride
);

export const canGoToSecondStep = createSelector(getCancellationState, (state) => {
  const requires = state.cancellationQuote.cancellationAdditionalInformation;
  const override = state.cancellationQuote.quoteOverrides;
  const isGdsRefund = state.activeGdsRefundSwitch;
  const isVoid = state.cancellationQuote.voidAllowed;

  if (requires && override) {
    const passPenalties = requires.requireShowPenalties ? override.proccesedPenalties : true;
    const passTaxes = requires.requireShowTaxes ? override.proccesedRefundTaxes : true;
    const passOriginalFare = requires.requireShowFareCalculation
      ? override.proccesedBaseFares
      : true;
    const passFixedCharges = isGdsRefund && !isVoid ? override.proccesedFixedCharge : true;
    return passPenalties && passTaxes && passOriginalFare && passFixedCharges;
  }
  console.warn('No hay suficiente informacion para saber si puede continuar');
  return true;
});

export const validateMachineLearningModel = createSelector(getCancellationState, (state) => {
  return state.errorMachineLearning;
});
